// src/lib/data/sync/SyncManager.js - V16 ENTERPRISE ULTRA-ROBUST
// ═══════════════════════════════════════════════════════════════════════════
// 🔄 SYNC MANAGER - V16 (AIRBNB / ZILLOW / QUINTOANDAR GRADE)
// ═══════════════════════════════════════════════════════════════════════════
// 🔥 V16: INDEXEDDB MIGRATION — offline_operations ahora usa IndexedDB
// 🔥 V16: queueOfflineOperation() → saveLog() / getLogs() (IndexedDB)
// 🔥 V16: COMPATIBLE HACIA ATRÁS — Si IndexedDB falla, usa localStorage
// 🔥 V15: STORAGE GUARDIAN — Protección contra cuota de localStorage excedida
// 🔥 V15: Limpieza automática de logs antiguos (> 7 días)
// 🔥 V15: Límite máximo de 50 entradas por tipo de log
// 🔥 V15: try/catch en TODAS las escrituras a localStorage
// 🔥 V15: Auto-recuperación si localStorage está lleno
// 🔥 V14: IDEMPOTENT UPDATE — Si update() falla por ID temporal, create()
// 🔥 V14: AUTO-HEALING SYNC — syncFromCloud() detecta y repara phantoms
// 🔥 V14: TEMP-ID REGISTRY — Registro central de IDs temporales
// 🔥 V14: PHANTOM DETECTOR — Detecta documentos huérfanos
// 🔥 V13: TOMBSTONE TRIPLE-LAYER (mem + LS + IndexedDB)
// ═══════════════════════════════════════════════════════════════════════════

import ConflictResolver from './ConflictResolver.js';
import { saveLog, getLogs } from '../../storage/indexedDB.js';

// ═══════════════════════════════════════════════════════════════════════════
// 📐 CONSTANTES DE SEGURIDAD
// ═══════════════════════════════════════════════════════════════════════════

const MAX_PULL_ITEMS        = 10;
const MAX_PULL_RATIO        = 3;
const GLOBAL_THROTTLE_MS    = 15_000;
const SAFETY_MARGIN_MS      = 5_000;
const RECENT_MODIFIED_MS    = 10_000;
const TOMBSTONE_CACHE_TTL   = 30_000;
const LS_TOMBSTONE_KEY      = 'imoveispro_tombstones_v13';
const TEMP_ID_REGISTRY_TTL  = 60_000;
const MAX_AUTO_REPAIR_RETRIES = 2;

// 🔥 V15: Límites de storage
const MAX_LOG_ENTRIES       = 50;
const LOG_RETENTION_DAYS    = 7;
const LOG_CLEANUP_INTERVAL  = 300_000;
const LS_QUOTA_SAFE_LIMIT   = 0.85;

// ═══════════════════════════════════════════════════════════════════════════
// 🛡️ STORAGE GUARDIAN — Protección contra cuota excedida
// ═══════════════════════════════════════════════════════════════════════════

function _checkStorageQuota() {
  try {
    let totalUsed = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      totalUsed += (localStorage.getItem(key)?.length || 0) * 2;
    }
    const estimatedQuota = 5 * 1024 * 1024;
    const usage = totalUsed / estimatedQuota;
    return { available: usage < LS_QUOTA_SAFE_LIMIT, usage, usedBytes: totalUsed, quotaBytes: estimatedQuota };
  } catch {
    return { available: false, usage: 1, usedBytes: 0, quotaBytes: 0 };
  }
}

function _safeSetItem(key, value, maxRetries = 2) {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  const store = (k, v) => {
    try { localStorage.setItem(k, v); return true; }
    catch (e) {
      if (e.name === 'QuotaExceededError' || e.message?.includes('exceeded the quota') || e.message?.includes('Setting the value')) return false;
      throw e;
    }
  };
  if (store(key, value)) return true;
  console.warn('⚠️ [V16 StorageGuardian] Cuota excedida — iniciando limpieza automática...');
  _emergencyLogCleanup();
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (store(key, value)) { console.log('✅ [V16 StorageGuardian] Escritura exitosa después de limpieza'); return true; }
    if (attempt < maxRetries - 1) _deepStorageCleanup();
  }
  console.error('❌ [V16 StorageGuardian] No se pudo escribir en localStorage después de limpieza');
  return false;
}

function _safeGetItem(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

function _safeRemoveItem(key) {
  try { localStorage.removeItem(key); } catch { /* ignorar */ }
}

function _emergencyLogCleanup() {
  const keysToClean = [];
  const now = Date.now();
  const maxAge = LOG_RETENTION_DAYS * 24 * 60 * 60 * 1000;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('conflict_log_') || key?.startsWith('sync_log_') || key?.startsWith('tombstone_log_') || key?.startsWith('sync_error_') || key?.startsWith('temp_sync_')) {
        keysToClean.push(key);
      }
    }
    const cleaned = [];
    for (const key of keysToClean) {
      try {
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        const timestamp = data._timestamp || data.timestamp || data.createdAt || 0;
        const age = now - new Date(timestamp).getTime();
        if (age > maxAge || cleaned.length < keysToClean.length - MAX_LOG_ENTRIES) {
          localStorage.removeItem(key);
          cleaned.push(key);
        }
      } catch { localStorage.removeItem(key); cleaned.push(key); }
    }
    if (cleaned.length > 0) console.log(`🧹 [V16 StorageGuardian] ${cleaned.length} logs antiguos eliminados`);
  } catch (e) { console.warn('⚠️ [V16 StorageGuardian] Error en limpieza de emergencia:', e.message); }
}

function _deepStorageCleanup() {
  const criticalPrefixes = ['imoveispro_db_user_', 'auth_user', 'imoveispro_tombstones'];
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      const isCritical = criticalPrefixes.some(prefix => key.startsWith(prefix));
      if (isCritical) continue;
      if (key.startsWith('conflict_') || key.startsWith('sync_') || key.startsWith('tombstone_') || key.startsWith('temp_') || key.startsWith('cache_') || key.startsWith('offline_operations') || key.startsWith('backup_') || key.startsWith('log_')) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) _safeRemoveItem(key);
    if (keysToRemove.length > 0) console.log(`🧹 [V16 StorageGuardian] Limpieza profunda: ${keysToRemove.length} entradas eliminadas`);
  } catch (e) { console.warn('⚠️ [V16 StorageGuardian] Error en limpieza profunda:', e.message); }
}

let _periodicCleanupInterval = null;
function _startPeriodicCleanup() {
  if (_periodicCleanupInterval) return;
  _periodicCleanupInterval = setInterval(() => { const quota = _checkStorageQuota(); if (!quota.available) _emergencyLogCleanup(); }, LOG_CLEANUP_INTERVAL);
}
function _stopPeriodicCleanup() {
  if (_periodicCleanupInterval) { clearInterval(_periodicCleanupInterval); _periodicCleanupInterval = null; }
}
if (typeof window !== 'undefined') {
  _startPeriodicCleanup();
  const initialQuota = _checkStorageQuota();
  if (!initialQuota.available) { console.warn(`⚠️ [V16 StorageGuardian] Cuota alta detectada al inicio: ${(initialQuota.usage * 100).toFixed(1)}%`); _emergencyLogCleanup(); }
}

// ═══════════════════════════════════════════════════════════════════════════
// 🛠️ UTILITARIOS INTERNOS
// ═══════════════════════════════════════════════════════════════════════════

function _quickHash(obj) {
  if (!obj) return '0';
  const str = JSON.stringify(obj, _stableReplacer);
  let h = 5381;
  for (let i = 0; i < str.length; i++) { h = ((h << 5) + h) ^ str.charCodeAt(i); h = h >>> 0; }
  return h.toString(36);
}
function _stableReplacer(key, value) {
  const IGNORE = new Set(['_lastSyncedAt', '_syncedAt', '_version', '_synced', '_cloudId', '_mergedAt', '_mergedFrom', '_migratedAt', '_migratedFrom', '_offline', '_offlineId', '_queued', '_fallback', 'updatedAt', 'createdAt']);
  return IGNORE.has(key) ? undefined : value;
}
function _ts(item, field) {
  if (!item) return 0;
  const v = item[field];
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return new Date(v).getTime() || 0;
  if (v?.toDate) return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  return 0;
}
const _sleep = (ms) => new Promise(r => setTimeout(r, ms));
function _backoff(attempt, base = 1000, cap = 30_000) {
  const exp = Math.min(base * Math.pow(2, attempt), cap);
  return exp * (0.5 + Math.random() * 0.5);
}
function _hasRealData(item) {
  if (!item) return false;
  return !!(item.title || item.name || item.description || item.value !== undefined || item.amount !== undefined || item.price !== undefined || item.type || item.status);
}

// ═══════════════════════════════════════════════════════════════════════════
// 🪦 LECTURA DE TOMBSTONES
// ═══════════════════════════════════════════════════════════════════════════

function _lsGetTombstonedIds(collection) {
  try {
    const raw = _safeGetItem(LS_TOMBSTONE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    const now = Date.now();
    const TTL = 30 * 24 * 60 * 60 * 1000;
    const ids = new Set();
    for (const [id, meta] of Object.entries(parsed)) {
      if (!meta.deletedAt || (now - meta.deletedAt) >= TTL) continue;
      if (!meta.collection || meta.collection === collection) ids.add(String(id));
    }
    return ids;
  } catch { return new Set(); }
}

// ═══════════════════════════════════════════════════════════════════════════
// ⏱️ TTLMap — Map con expiración automática
// ═══════════════════════════════════════════════════════════════════════════

class TTLMap {
  constructor(defaultTTL = 10_000) { this._map = new Map(); this._ttl = defaultTTL; }
  set(key, value, ttl) { this._map.set(key, { value, expires: Date.now() + (ttl ?? this._ttl) }); }
  get(key) { const entry = this._map.get(key); if (!entry) return undefined; if (Date.now() > entry.expires) { this._map.delete(key); return undefined; } return entry.value; }
  has(key) { return this.get(key) !== undefined; }
  delete(key) { this._map.delete(key); }
  get size() { this._evict(); return this._map.size; }
  _evict() { const now = Date.now(); for (const [k, v] of this._map) if (now > v.expires) this._map.delete(k); }
  clear() { this._map.clear(); }
}

// ═══════════════════════════════════════════════════════════════════════════
// 🔄 SyncManager V16 ULTRA-ROBUST
// ═══════════════════════════════════════════════════════════════════════════

export class SyncManager {
  constructor(userId, strategy = null, options = {}) {
    this.userId        = userId;
    this.strategy      = strategy;
    this.queue         = [];
    this.isSyncing     = false;
    this.isOnline      = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.syncInterval  = null;
    this.maxRetries    = options.maxRetries   ?? 3;
    this.retryDelay    = options.retryDelay   ?? 1_000;
    this.syncCooldown  = options.syncCooldown ?? 30_000;
    this.versionField  = '_version';
    this.lastSyncField = '_lastSyncedAt';
    this.observers     = [];
    this.cloudStrategy = null;
    this.localStrategy = null;
    this.strategyType  = this.detectStrategyType(strategy);

    this._resolveStrategies(strategy);

    this.idMapping          = new Map();
    this.pendingCreates     = new Map();
    this.pullCooldown       = new TTLMap(this.syncCooldown * 2);
    this.recentlyExecuted   = new TTLMap(2_000);
    this.contentHash        = new TTLMap(60_000);
    this.permissionDeniedDocs = new Set();
    this._globalThrottle    = new TTLMap(GLOBAL_THROTTLE_MS);
    this.recentlyModified   = new TTLMap(RECENT_MODIFIED_MS);
    this._tempIdRegistry    = new Map();
    this._autoRepairStats   = { total: 0, byCollection: {} };
    this.conflictResolver   = new ConflictResolver(userId, this.localStrategy);
    this._tombstonedIdsCache = new Map();
    this._beforePullHook    = null;

    this._onOnline  = this._handleOnline.bind(this);
    this._onOffline = this._handleOffline.bind(this);
    if (typeof window !== 'undefined') {
      window.addEventListener('online',  this._onOnline);
      window.addEventListener('offline', this._onOffline);
    }

    this.startSyncInterval();
    this._tempIdCleanupInterval = setInterval(() => this._cleanupTempIdRegistry(), 120_000);

    const quota = _checkStorageQuota();
    if (!quota.available) {
      console.warn(`⚠️ [V16] Cuota de localStorage alta al iniciar SyncManager: ${(quota.usage * 100).toFixed(1)}%`);
    }

    console.log(
      `🔄 SyncManager V16 — user: ${userId} | strategy: ${this.strategyType}`,
      `| cloud: ${!!this.cloudStrategy} | local: ${!!this.localStrategy}`,
      `| 🛡️ STORAGE GUARDIAN | 🗄️ INDEXEDDB OFFLINE OPS | 🪦 TOMBSTONE TRIPLE-LAYER | 🔧 AUTO-REPAIR | 👻 PHANTOM DETECTOR`
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔥 V14: REGISTRO DE IDs TEMPORALES
  // ═══════════════════════════════════════════════════════════════════════

  _registerTempId(tempId, collection) {
    if (!tempId || !collection) return;
    const key = String(tempId);
    if (!this._tempIdRegistry.has(key)) {
      this._tempIdRegistry.set(key, { timestamp: Date.now(), collection, resolvedId: null, repairAttempts: 0 });
    }
  }

  _resolveTempId(tempId, realId) {
    if (!tempId || !realId) return;
    const key = String(tempId);
    const entry = this._tempIdRegistry.get(key);
    if (entry) { entry.resolvedId = String(realId); entry.resolvedAt = Date.now(); }
  }

  _cleanupTempIdRegistry() {
    const now = Date.now();
    const toDelete = [];
    for (const [key, entry] of this._tempIdRegistry.entries()) {
      if (now - entry.timestamp > TEMP_ID_REGISTRY_TTL) toDelete.push(key);
    }
    for (const key of toDelete) this._tempIdRegistry.delete(key);
    if (toDelete.length > 0) console.log(`🧹 [V16] TempID Registry: ${toDelete.length} entradas expiradas eliminadas`);
  }

  async _detectPhantoms(collection, localItems, cloudItems) {
    if (!Array.isArray(localItems) || !Array.isArray(cloudItems)) return [];
    const cloudIds = new Set(cloudItems.map(i => String(i.id)));
    const phantoms = [];
    for (const item of localItems) {
      const id = String(item.id || '');
      if (this.isTempId(id) && !cloudIds.has(id) && !this.idMapping.has(id)) phantoms.push({ id, collection, item });
    }
    if (phantoms.length > 0) console.log(`👻 [V16 PHANTOM] ${phantoms.length} documentos huérfanos detectados en "${collection}"`);
    return phantoms;
  }

  async _repairPhantoms(collection, phantoms) {
    if (!phantoms.length || !this.cloudStrategy || !this.isOnline) return 0;
    let repaired = 0;
    for (const phantom of phantoms) {
      try {
        const cleanData = { ...phantom.item };
        delete cleanData.id; delete cleanData._localId; delete cleanData._syncStatus;
        const result = await this._strategyCreate(this.cloudStrategy, collection, cleanData);
        const realId = result?.id ?? result?.docId ?? result?._id;
        if (realId && String(realId) !== phantom.id) {
          this.registerIdMapping(phantom.id, realId);
          this._resolveTempId(phantom.id, realId);
          if (this.localStrategy) {
            await this._safeLocalUpdate(collection, phantom.id, { ...phantom.item, id: realId, _cloudId: realId, _syncedAt: new Date().toISOString(), _syncStatus: 'synced' }).catch(() => {});
          }
          this._autoRepairStats.total++;
          this._autoRepairStats.byCollection[collection] = (this._autoRepairStats.byCollection[collection] || 0) + 1;
          repaired++;
          console.log(`🔧 [V16 REPAIR] Phantom reparado: ${collection}/${phantom.id} → ${realId}`);
        }
      } catch (err) { console.warn(`⚠️ [V16] No se pudo reparar phantom ${collection}/${phantom.id}:`, err.message); }
    }
    return repaired;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔧 INICIALIZACIÓN Y VÍNCULOS
  // ═══════════════════════════════════════════════════════════════════════

  _resolveStrategies(strategy) {
    if (!strategy) return;
    if (strategy.cloud && strategy.local) {
      this.cloudStrategy = strategy.cloud;
      this.localStrategy = strategy.local;
      this._bindLocalStrategyToConflictResolver();
    } else if (strategy.constructor?.name === 'CloudStrategy') {
      this.cloudStrategy = strategy;
    } else if (strategy.constructor?.name === 'LocalStrategy') {
      this.localStrategy = strategy;
      this._bindLocalStrategyToConflictResolver();
    } else {
      this._extractStrategiesFromContext(strategy);
    }
  }

  _extractStrategiesFromContext(context) {
    try {
      if (!context || typeof context !== 'object') return;
      const s = typeof context.getStrategy === 'function' ? context.getStrategy() : null;
      if (!s) return;
      if (s.cloud && s.local) { this.cloudStrategy = s.cloud; this.localStrategy = s.local; this._bindLocalStrategyToConflictResolver(); }
      else if (s.constructor?.name === 'CloudStrategy') this.cloudStrategy = s;
      else if (s.constructor?.name === 'LocalStrategy') { this.localStrategy = s; this._bindLocalStrategyToConflictResolver(); }
    } catch (e) { console.warn('⚠️ _extractStrategiesFromContext:', e.message); }
  }

  extractStrategiesFromContext(context) { return this._extractStrategiesFromContext(context); }

  _bindLocalStrategyToConflictResolver() {
    if (this.localStrategy && this.conflictResolver && typeof this.conflictResolver.setLocalStrategy === 'function') {
      this.conflictResolver.setLocalStrategy(this.localStrategy);
      console.log('🔗 [V16] ConflictResolver ← LocalStrategy vinculado');
    }
  }

  detectStrategyType(strategy) {
    if (!strategy) return 'none';
    const n = strategy.constructor?.name || '';
    if (strategy.cloud && strategy.local) return 'dual';
    if (n.includes('DualSyncStrategy')) return 'dual';
    if (n.includes('CloudStrategy') || strategy.type === 'cloud') return 'cloud';
    if (n.includes('LocalStrategy')  || strategy.type === 'local') return 'local';
    return 'unknown';
  }

  setCloudStrategy(s) { if (s) this.cloudStrategy = s; }
  setLocalStrategy(s) {
    if (s) { this.localStrategy = s; this._bindLocalStrategyToConflictResolver(); this._invalidateTombstoneCache(); console.log('🔗 [V16] SyncManager ← LocalStrategy vinculado'); }
  }
  setStrategy(strategy) {
    this.strategy = strategy; this.strategyType = this.detectStrategyType(strategy);
    this.cloudStrategy = null; this.localStrategy = null;
    this._resolveStrategies(strategy); this.notify('strategy:updated', { strategyType: this.strategyType });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔥 REGISTRO DE IDS
  // ═══════════════════════════════════════════════════════════════════════

  registerIdMapping(tempId, realId) {
    if (!tempId || !realId || String(tempId) === String(realId)) return;
    this.idMapping.set(String(tempId), String(realId));
    this._resolveTempId(tempId, realId);
    const resolve = this.pendingCreates.get(String(tempId));
    if (typeof resolve === 'function') { resolve(realId); this.pendingCreates.delete(String(tempId)); }
  }

  resolveRealId(id) { if (!id) return id; const resolved = this.idMapping.get(String(id)); return resolved ?? id; }

  isTempId(id) {
    if (!id) return false;
    const s = String(id);
    if (this.idMapping.has(s)) return false;
    return s.startsWith('temp-') || s.startsWith('temp_') || s.startsWith('offline_');
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔥 HOOK PRÉ-PULL
  // ═══════════════════════════════════════════════════════════════════════

  setBeforePullHook(hook) { if (typeof hook === 'function') { this._beforePullHook = hook; console.log('🔗 [V16] Hook onBeforePull registrado no SyncManager'); } }

  async _applyBeforePullHook(collection, records) {
    if (!this._beforePullHook || !Array.isArray(records)) return records;
    try {
      const filtered = await this._beforePullHook(collection, records);
      const removed = records.length - (filtered?.length ?? 0);
      if (removed > 0) console.log(`🔗 [V16 HOOK] beforePullHook filtrou ${removed} registros de "${collection}"`);
      return filtered ?? records;
    } catch (e) { console.warn('⚠️ [V16] beforePullHook falhou, continuando sem filtro:', e.message); return records; }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🪦 TOMBSTONES TRIPLE-LAYER
  // ═══════════════════════════════════════════════════════════════════════

  async _getTombstonedIds(collection) {
    const now = Date.now();
    const cached = this._tombstonedIdsCache.get(collection);
    const memIds = (cached && (now - cached.timestamp) < TOMBSTONE_CACHE_TTL) ? cached.ids : new Set();
    const lsIds = _lsGetTombstonedIds(collection);
    let dbIds = new Set();
    if (!cached || (now - cached.timestamp) >= TOMBSTONE_CACHE_TTL) {
      try {
        if (this.localStrategy && typeof this.localStrategy.getTombstonedIds === 'function') {
          dbIds = await this.localStrategy.getTombstonedIds(collection) || new Set();
          this._tombstonedIdsCache.set(collection, { ids: dbIds, timestamp: now });
        }
      } catch (e) { this._tombstonedIdsCache.set(collection, { ids: lsIds, timestamp: now }); }
    }
    return new Set([...[ ...memIds].map(String), ...[...lsIds].map(String), ...[...dbIds].map(String)]);
  }

  async _isTombstonedInLocal(collection, id) {
    if (!collection || !id) return false;
    try { if (_lsGetTombstonedIds(collection).has(String(id))) return true; const ids = await this._getTombstonedIds(collection); return ids.has(String(id)); }
    catch { return false; }
  }

  _invalidateTombstoneCache(collection = null) { if (collection) this._tombstonedIdsCache.delete(collection); else this._tombstonedIdsCache.clear(); }

  notifyDelete(collection, id) {
    this._invalidateTombstoneCache(collection);
    this.pullCooldown.delete(`${collection}:${String(id)}`);
    this.contentHash.delete(`${collection}:${String(id)}`);
    this.pullCooldown.delete(`checkUpdates:${collection}`);
  }

  async _filterCloudDataByTombstones(cloudItems, collection) {
    if (!Array.isArray(cloudItems) || cloudItems.length === 0) return cloudItems;
    try {
      const tombstonedIds = await this._getTombstonedIds(collection);
      if (tombstonedIds.size === 0) return cloudItems;
      const filtered = [], blocked = [];
      for (const item of cloudItems) { const itemId = String(item?.id || ''); if (tombstonedIds.has(itemId)) blocked.push(itemId); else filtered.push(item); }
      if (blocked.length > 0) console.log(`🛡️ [V16 TOMBSTONE] ${blocked.length} ressurreições bloqueadas em "${collection}"`);
      return filtered;
    } catch (e) { return cloudItems; }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔥 ESCRITAS SEGURAS
  // ═══════════════════════════════════════════════════════════════════════

  async _safeLocalCreate(collection, data) {
    const id = String(data?.id || '');
    const [filtered] = await this._applyBeforePullHook(collection, [data]);
    if (!filtered) return null;
    if (id && await this._isTombstonedInLocal(collection, id)) return null;
    return this.localStrategy.create(collection, filtered);
  }

  async _safeLocalUpdate(collection, id, data) {
    const strId = String(id || '');
    const [filtered] = await this._applyBeforePullHook(collection, [{ ...data, id: strId }]);
    if (!filtered) return null;
    if (strId && await this._isTombstonedInLocal(collection, strId)) return null;
    return this._strategyUpdate(this.localStrategy, collection, id, filtered);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🛡️ MARCAJE DE ITEMS MODIFICADOS
  // ═══════════════════════════════════════════════════════════════════════

  markRecentlyModified(id) { if (id) this.recentlyModified.set(String(id), true); }
  _isRecentlyModified(id) { return this.recentlyModified.has(String(id)); }

  // ═══════════════════════════════════════════════════════════════════════
  // 🌐 REDE
  // ═══════════════════════════════════════════════════════════════════════

  _handleOnline() { this.isOnline = true; console.log('🌐 [V16] Online — procesando cola...'); this.processQueue(); }
  _handleOffline() { this.isOnline = false; }

  startSyncInterval() {
    this.stopSyncInterval();
    this.syncInterval = setInterval(() => { if (this.isOnline && !this.isSyncing && this.queue.length > 0) this.processQueue(); }, 30_000);
  }
  stopSyncInterval() { if (this.syncInterval) { clearInterval(this.syncInterval); this.syncInterval = null; } }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔍 ANTI-LOOP e ANTI-DUPLICADO
  // ═══════════════════════════════════════════════════════════════════════

  _shouldSkipPull(collection, id, cloudItem) {
    const key = `${collection}:${id}`;
    if (this.pullCooldown.has(key)) return true;
    if (this.contentHash.get(key) === _quickHash(cloudItem)) return true;
    return false;
  }
  _markPulled(collection, id, cloudItem) {
    const key = `${collection}:${id}`;
    this.pullCooldown.set(key, true, this.syncCooldown);
    this.contentHash.set(key, _quickHash(cloudItem), 60_000);
  }
  _validateOwnership(item) {
    if (!item) return false;
    if (item.userId && item.userId !== this.userId) return false;
    if (item._userId && item._userId !== this.userId) return false;
    if (item.createdBy && item.createdBy !== this.userId) return false;
    return true;
  }
  _isDuplicateOp(operation) {
    if (operation.type === 'CREATE') return false;
    const key = `${operation.type}_${operation.collection}_${operation.localId ?? operation.id ?? ''}`;
    return this.recentlyExecuted.has(key);
  }
  _markOpExecuted(operation) {
    if (operation.type === 'CREATE') return;
    const key = `${operation.type}_${operation.collection}_${operation.localId ?? operation.id ?? ''}`;
    this.recentlyExecuted.set(key, true);
  }
  isPermissionDenied(collection, id) { return this.permissionDeniedDocs.has(`${collection}:${id}`); }
  markPermissionDenied(collection, id) { this.permissionDeniedDocs.add(`${collection}:${id}`); }

  // ═══════════════════════════════════════════════════════════════════════
  // 🏷️ CHAVE DE TÍTULO
  // ═══════════════════════════════════════════════════════════════════════

  _getTitleKey(collection, item) {
    if (!item) return null;
    const name = (item.title ?? item.name ?? '').toLowerCase().trim();
    if (!name) return null;
    if (collection === 'properties') return item.price != null ? `prop:${name}:${item.price}` : `prop:${name}`;
    if (collection === 'clients') { const email = (item.email ?? '').toLowerCase().trim(); return email ? `client:${name}:${email}` : `client:${name}`; }
    if (collection === 'tasks') return `task:${name}`;
    return `${collection}:${name}`;
  }
  _findByTitleKey(list, collection, item) {
    const key = this._getTitleKey(collection, item);
    if (!key) return null;
    return list.find(c => this._getTitleKey(collection, c) === key) ?? null;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🎯 HELPERS DE ESTRATÉGIA
  // ═══════════════════════════════════════════════════════════════════════

  _getCloudStrategy() { return this.cloudStrategy ?? this.strategy?.cloud ?? (this.strategy?.constructor?.name === 'CloudStrategy' ? this.strategy : null); }
  _getEffectiveStrategy() { return this._getCloudStrategy() ?? this.strategy; }

  async _strategyRead(strategy, collection) {
    if (!strategy) return [];
    try { const fn = strategy.read ?? strategy.getAll; if (typeof fn === 'function') { const res = await fn.call(strategy, collection); return Array.isArray(res) ? res : []; } }
    catch (e) { console.warn(`⚠️ _strategyRead(${collection}):`, e.message); }
    return [];
  }
  async _strategyCreate(strategy, collection, item) {
    if (!strategy) return null;
    try { const fn = strategy.create ?? strategy.save; if (typeof fn === 'function') return await fn.call(strategy, collection, item); }
    catch (e) { console.warn(`⚠️ _strategyCreate(${collection}):`, e.message); }
    return null;
  }
  async _strategyUpdate(strategy, collection, id, data) {
    if (!strategy) return null;
    try { if (typeof strategy.update === 'function') return await strategy.update(collection, id, data); if (typeof strategy.save === 'function') return await strategy.save(collection, data, id); }
    catch (e) { throw e; }
    return null;
  }
  async _strategyDelete(strategy, collection, id) {
    if (!strategy) return null;
    try { if (typeof strategy.delete === 'function') return await strategy.delete(collection, id); }
    catch (e) { throw e; }
    return null;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 📥 FILA DE OPERAÇÕES
  // ═══════════════════════════════════════════════════════════════════════

  async enqueue(operation) {
    if (!operation.collection || !operation.type) throw new Error('enqueue: campos obrigatórios ausentes');
    if (this._isDuplicateOp(operation)) { console.log('⏭️ Op duplicada suprimida'); return null; }
    const opId = operation.localId ?? operation.data?.id ?? operation.data?._localId;
    if (opId && this.isTempId(opId)) this._registerTempId(opId, operation.collection);
    const op = { ...operation, _id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`, _userId: this.userId, _queuedAt: new Date().toISOString(), _retryCount: 0, _status: 'queued' };
    this.queue.push(op);
    if (this.isOnline && !this.isSyncing) setTimeout(() => this.processQueue(), 100);
    return op._id;
  }
  async addToQueue(op) { return this.enqueue(op); }
  async create(collection, data) {
    const localId = data?.id || data?._localId;
    if (localId && this.isTempId(localId)) this._registerTempId(localId, collection);
    return this.enqueue({ collection, type: 'CREATE', data, timestamp: new Date().toISOString(), userId: this.userId });
  }
  async update(collection, documentId, data) {
    return this.enqueue({ collection, type: 'UPDATE', localId: documentId, data, timestamp: new Date().toISOString(), userId: this.userId });
  }
  async delete(collection, documentId) {
    this._invalidateTombstoneCache(collection);
    this.notifyDelete(collection, documentId);
    return this.enqueue({ collection, type: 'DELETE', localId: documentId, data: { _id: documentId, _deleted: true, _deletedAt: new Date().toISOString(), _deletedBy: this.userId }, timestamp: new Date().toISOString(), userId: this.userId });
  }
  async save(collection, operation) {
    return this.enqueue({ collection, type: 'SAVE', data: operation.data, id: operation.id, localId: operation.id, timestamp: Date.now(), userId: this.userId });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔄 PROCESSAMENTO DA FILA
  // ═══════════════════════════════════════════════════════════════════════

  async processQueue() {
    if (this.isSyncing || !this.queue.length || !this.isOnline) return;
    this.isSyncing = true;
    while (this.queue.length > 0 && this.isOnline) {
      const op = this.queue[0];
      try { await this.executeOperation(op); this.queue.shift(); }
      catch (err) {
        op._retryCount++; op._status = 'failed'; op._lastError = err.message;
        if (op._retryCount >= this.maxRetries) { console.log(`❌ Op descartada após ${this.maxRetries} tentativas: ${op.collection}/${op.localId}`); this.queue.shift(); }
        else { this.queue.shift(); this.queue.push(op); await _sleep(_backoff(op._retryCount - 1, this.retryDelay)); }
      }
    }
    this.isSyncing = false;
  }
  async flushQueue() { if (!this.isOnline) return; return this.processQueue(); }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔥 V14: EXECUÇÃO DE OPERAÇÃO COM AUTO-REPAIR
  // ═══════════════════════════════════════════════════════════════════════

  async executeOperation(op) {
    op._status = 'processing'; op._startedAt = new Date().toISOString();
    try {
      if (op.type === 'UPDATE' || op.type === 'DELETE') {
        const resolved = this.resolveRealId(op.localId ?? op.id);
        if (String(resolved) !== String(op.localId ?? op.id)) { op.localId = resolved; if (op.data) op.data.id = resolved; }
        if (this.isTempId(op.localId) && this.cloudStrategy) { console.log(`🔄 [V16] UPDATE con ID temporal "${op.localId}" → convirtiendo a CREATE`); op = { ...op, type: 'CREATE', data: this._stripSyncMeta(op.data) }; }
      }
      this._markOpExecuted(op);
      let result;
      switch (op.type) {
        case 'CREATE': case 'UPDATE': case 'DELETE': result = await this.executeCrudOperation(op); break;
        case 'SAVE': result = await this.executeSaveOperation(op); break;
        default: result = await this.syncToCloud(op);
      }
      op._status = 'completed'; op._completedAt = new Date().toISOString(); op._result = result;
      return result;
    } catch (err) {
      if (op.type === 'UPDATE' && this.isTempId(op.localId) && op._retryCount < MAX_AUTO_REPAIR_RETRIES) {
        console.warn(`🔄 [V16 RETRY] UPDATE falló para ID temporal "${op.localId}", reintentando como CREATE...`);
        op.type = 'CREATE'; op.data = this._stripSyncMeta(op.data); op._retryCount++; this.queue.unshift(op); throw err;
      }
      throw err;
    }
  }
  _stripSyncMeta(data) {
    if (!data) return {};
    const clean = { ...data };
    delete clean.id; delete clean._version; delete clean._lastSyncedAt; delete clean._syncedAt; delete clean._synced; delete clean._cloudId; delete clean._mergedFrom; delete clean._mergedAt;
    return clean;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔥 executeCrudOperation
  // ═══════════════════════════════════════════════════════════════════════

  async executeCrudOperation(op) {
    const { collection, type } = op;
    let { data, localId } = op;
    const cloud = this._getCloudStrategy();
    if (!cloud) return this._executeLocalFallback(op);
    try {
      let result;
      switch (type) {
        case 'CREATE': {
          const docId = String(localId ?? data?.id ?? data?._localId ?? `offline_${Date.now()}`);
          const { doc: firestoreDoc, getDoc, setDoc, updateDoc } = await import('firebase/firestore');
          const collectionPath = cloud._isGlobalCollection?.(collection) ? collection : `companies/${cloud.companyId}/${collection}`;
          const docRef = firestoreDoc(cloud.db, collectionPath, docId);
          const existingSnap = await getDoc(docRef);
          if (existingSnap.exists()) {
            const existingData = existingSnap.data();
            await updateDoc(docRef, { ...data, id: docId, _syncStatus: 'synced', _syncedAt: new Date().toISOString(), _version: (existingData._version || 0) + 1, updatedAt: new Date().toISOString(), updatedBy: this.userId, createdAt: existingData.createdAt || data.createdAt, createdBy: existingData.createdBy || data.createdBy });
          } else {
            await setDoc(docRef, { ...data, id: docId, _syncStatus: 'synced', _syncedAt: new Date().toISOString(), _version: 1, createdAt: data.createdAt || new Date().toISOString(), createdBy: data.createdBy || this.userId, updatedAt: new Date().toISOString(), updatedBy: this.userId, companyId: cloud.companyId });
          }
          result = { id: docId, ...data, _syncStatus: 'synced' };
          const tempId = String(localId ?? data?.id ?? '');
          if (tempId && docId !== tempId) { this.registerIdMapping(tempId, docId); this._resolveTempId(tempId, docId); }
          break;
        }
        case 'UPDATE': {
          if (this.isTempId(localId)) return this.executeCrudOperation({ ...op, type: 'CREATE', data: this._stripSyncMeta(data) });
          result = await this._strategyUpdate(cloud, collection, localId, data);
          break;
        }
        case 'DELETE': {
          if (this.localStrategy && typeof this.localStrategy.createTombstone === 'function') await this.localStrategy.createTombstone(collection, localId, { userId: this.userId, reason: 'sync_delete' });
          this._invalidateTombstoneCache(collection); this.notifyDelete(collection, localId);
          result = await this._strategyDelete(cloud, collection, localId);
          break;
        }
      }
      return result;
    } catch (err) {
      if (err.code === 'permission-denied' || err.message?.includes('Missing or insufficient permissions')) { this.markPermissionDenied(collection, localId); throw err; }
      return this._executeLocalFallback(op);
    }
  }

  async executeSaveOperation(op) {
    const cloud = this._getCloudStrategy();
    if (!cloud) throw new Error('Sem cloud strategy para SAVE');
    if (typeof cloud.save === 'function') return cloud.save(op.collection, op.data, op.id);
    return this.executeCrudOperation({ ...op, type: op.id ? 'UPDATE' : 'CREATE', localId: op.id });
  }

  async _executeLocalFallback(op) {
    if (!this.localStrategy) throw new Error('Sem estratégia disponível');
    const { type, collection, localId, data } = op;
    if (type === 'UPDATE') return this.localStrategy.update(collection, localId, data);
    if (type === 'CREATE') return this.localStrategy.create(collection, data);
    if (type === 'DELETE') {
      if (typeof this.localStrategy.createTombstone === 'function') await this.localStrategy.createTombstone(collection, localId, { userId: this.userId, reason: 'fallback_delete' });
      this._invalidateTombstoneCache(collection);
      return this.localStrategy.delete(collection, localId);
    }
    throw new Error(`LocalStrategy não suporta ${type}`);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ☁️ SINCRONIZACIÓN BIDIRECCIONAL — V16 BLINDADO
  // ═══════════════════════════════════════════════════════════════════════

  checkForUpdates(collection, localData) {
    const globalKey = `global:check:${collection}`;
    if (this._globalThrottle.has(globalKey)) return { hasPendingChanges: false, pendingCount: 0, collection, throttled: true };
    this._globalThrottle.set(globalKey, true);
    const pending = this.queue.filter(i => i.collection === collection && i._status === 'queued');
    if (pending.length > 0 && this.isOnline) this.flushQueue();
    if (this.isOnline && this.cloudStrategy && this.localStrategy) {
      const cooldownKey = `checkUpdates:${collection}`;
      if (!this.pullCooldown.has(cooldownKey)) { this.pullCooldown.set(cooldownKey, true, this.syncCooldown); this.syncFromCloud(collection, localData).catch(e => console.error(`❌ syncFromCloud(${collection}):`, e.message)); }
    }
    return { hasPendingChanges: pending.length > 0, pendingCount: pending.length, collection, localDataCount: localData?.length ?? 0 };
  }

  async syncFromCloud(collection, localData = []) {
    if (!this.cloudStrategy || !this.localStrategy) return { updates: 0, conflicts: 0, pushed: 0, version: 'V16' };
    try {
      this._invalidateTombstoneCache(collection);
      const [cloudRaw, freshLocal] = await Promise.all([this._strategyRead(this.cloudStrategy, collection), this._strategyRead(this.localStrategy, collection)]);
      let cloudData = (cloudRaw ?? []).filter(item => this._validateOwnership(item));
      const localItems = freshLocal.length ? freshLocal : localData;
      const phantoms = await this._detectPhantoms(collection, localItems, cloudData);
      if (phantoms.length > 0 && this.isOnline) await this._repairPhantoms(collection, phantoms);
      const beforeFilterCount = cloudData.length;
      cloudData = await this._filterCloudDataByTombstones(cloudData, collection);
      cloudData = await this._applyBeforePullHook(collection, cloudData);
      const blockedCount = beforeFilterCount - cloudData.length;
      let cloudItems = cloudData;
      if (localItems.length > 0 && cloudItems.length > localItems.length * MAX_PULL_RATIO) cloudItems = cloudItems.slice(0, MAX_PULL_ITEMS);
      const localById = new Map(localItems.map(i => [String(i.id), i]));
      const cloudById = new Map(cloudItems.map(i => [String(i.id), i]));
      const localByTitle = new Map();
      localItems.forEach(i => { const k = this._getTitleKey(collection, i); if (k) localByTitle.set(k, i); });
      let updates = 0, conflicts = 0, pushed = 0, pulledThisSync = 0;

      for (const localItem of localItems) {
        const id = String(localItem.id);
        if (await this._isTombstonedInLocal(collection, id)) continue;
        if (cloudById.has(id)) continue;
        const cloudMatch = this._findByTitleKey(cloudItems, collection, localItem);
        if (cloudMatch) {
          const resolved = await this.conflictResolver.resolve(collection, localItem, cloudMatch);
          await this._safeLocalUpdate(collection, id, { ...resolved, _mergedFrom: id, _mergedAt: new Date().toISOString() });
          this.registerIdMapping(id, cloudMatch.id); updates++;
        } else {
          const cleanData = { ...localItem };
          if (this.isTempId(id)) delete cleanData.id;
          try {
            const result = await this._strategyCreate(this.cloudStrategy, collection, cleanData);
            const realId = result?.id ?? result?.docId ?? result?._id;
            if (realId && String(realId) !== id) { this.registerIdMapping(id, realId); await this._safeLocalUpdate(collection, id, { ...localItem, id: realId, _cloudId: realId }).catch(() => {}); }
            pushed++;
          } catch (err) { conflicts++; }
        }
      }

      const refreshedLocal = await this._strategyRead(this.localStrategy, collection);
      const refreshedItems = refreshedLocal.length ? refreshedLocal : localItems;
      const refreshedById = new Map(refreshedItems.map(i => [String(i.id), i]));
      const refreshedByTitle = new Map();
      refreshedItems.forEach(i => { const k = this._getTitleKey(collection, i); if (k) refreshedByTitle.set(k, i); });

      for (const cloudItem of cloudItems) {
        if (pulledThisSync >= MAX_PULL_ITEMS) break;
        if (!this._validateOwnership(cloudItem)) continue;
        const id = String(cloudItem.id);
        if (await this._isTombstonedInLocal(collection, id)) continue;
        const localItem = refreshedById.get(id);
        if (!localItem) {
          const titleKey = this._getTitleKey(collection, cloudItem);
          const byTitle = titleKey ? refreshedByTitle.get(titleKey) : null;
          if (byTitle) {
            if (this._shouldSkipPull(collection, String(byTitle.id), cloudItem)) continue;
            if (await this._isTombstonedInLocal(collection, String(byTitle.id))) continue;
            if (await this._isTombstonedInLocal(collection, id)) continue;
            const localByTitleId = refreshedItems.find(i => String(i.id) === String(byTitle.id));
            const yaSincronizado = localByTitleId?._cloudId === id;
            if (yaSincronizado) { this._markPulled(collection, id, cloudItem); updates++; pulledThisSync++; }
            else { const updated = await this._safeLocalUpdate(collection, byTitle.id, { ...cloudItem, id: byTitle.id, _cloudId: id, _mergedAt: new Date().toISOString() }); if (updated !== null) { updates++; pulledThisSync++; } }
          } else {
            if (refreshedItems.some(i => String(i.id) === id)) continue;
            if (this._shouldSkipPull(collection, id, cloudItem)) continue;
            if (await this._isTombstonedInLocal(collection, id)) continue;
            const created = await this._safeLocalCreate(collection, { ...cloudItem, id });
            if (created !== null) { updates++; pulledThisSync++; }
          }
        } else {
          if (await this._isTombstonedInLocal(collection, id)) continue;
          const resolved = await this.conflictResolver.resolve(collection, localItem, cloudItem);
          if (resolved._deleted || resolved._tombstone) continue;
          const cloudTime = _ts(cloudItem, 'updatedAt') || _ts(cloudItem, 'createdAt');
          const localTime = _ts(localItem, 'updatedAt') || _ts(localItem, 'createdAt');
          if (cloudTime > localTime) {
            if (!_hasRealData(cloudItem)) continue;
            if (this._isRecentlyModified(id)) continue;
            if (cloudTime <= localTime + SAFETY_MARGIN_MS) continue;
            if (this._shouldSkipPull(collection, id, cloudItem)) continue;
            const updated = await this._safeLocalUpdate(collection, id, { ...resolved, _lastPulledAt: new Date().toISOString() });
            if (updated !== null) { updates++; pulledThisSync++; }
          } else if (localTime > cloudTime) {
            if (this.isPermissionDenied(collection, id)) { conflicts++; continue; }
            try { await this._strategyUpdate(this.cloudStrategy, collection, id, resolved); pushed++; }
            catch (err) { conflicts++; }
          }
        }
      }

      return { updates, pushed, conflicts, pulledCount: pulledThisSync, phantomsDetected: phantoms.length, phantomsRepaired: phantoms.length, blockedByTombstone: blockedCount || 0, version: 'V16' };
    } catch (err) {
      if (err.message?.includes('exceeded the quota') || err.message?.includes('Setting the value')) { console.warn('⚠️ [V16] Cuota excedida durante syncFromCloud — limpiando logs...'); _emergencyLogCleanup(); }
      console.error(`❌ [V16] syncFromCloud(${collection}):`, err.message);
      return { error: err.message, version: 'V16' };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 🔧 UTILITARIOS
  // ═══════════════════════════════════════════════════════════════════════

  async syncToCloud(operation) {
    const s = this._getEffectiveStrategy();
    if (!s) return this.queueOfflineOperation(operation);
    const { collection, data, type, localId } = operation;
    try {
      switch (type) {
        case 'CREATE': return await this._strategyCreate(s, collection, data);
        case 'UPDATE': return await this._strategyUpdate(s, collection, localId, data);
        case 'DELETE': return await this._strategyDelete(s, collection, localId);
        default: throw new Error(`Tipo desconhecido: ${type}`);
      }
    } catch (err) {
      if (err.code === 'unavailable' || !navigator.onLine) return this.queueOfflineOperation(operation);
      throw err;
    }
  }

  /**
   * 🔥 V16: Guarda operación offline en IndexedDB con fallback a localStorage.
   * 
   * Flujo:
   *   1. Intentar guardar en IndexedDB (saveLog)
   *   2. Si falla, usar localStorage (_safeSetItem) como fallback
   *   3. Retorna el objeto de operación con _offline: true
   */
  async queueOfflineOperation(operation) {
    const op = {
      ...operation,
      _status:    'offline',
      _queuedAt:  new Date().toISOString(),
      _offlineId: `offline_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    };

    // 🔥 V16: Intentar IndexedDB primero
    try {
      const existingLogs = await getLogs(this.userId, 'offline_operations', 100);
      let existing = [];
      
      // Reconstruir el array de operaciones desde los logs
      if (existingLogs.length > 0) {
        try {
          const latestLog = existingLogs[0];
          existing = JSON.parse(latestLog.data || '[]');
        } catch { existing = []; }
      }
      
      existing.push(op);
      const trimmed = existing.slice(-50); // Máximo 50 operaciones offline
      
      await saveLog(this.userId, 'offline_operations', JSON.stringify(trimmed));
      console.log(`📦 [V16] Operación offline guardada en IndexedDB (${trimmed.length} total)`);
      
      return { ...operation.data, _offline: true, _offlineId: op._offlineId };
    } catch (indexedDBError) {
      // 🔥 FALLBACK: localStorage (comportamiento original V15)
      console.warn('⚠️ [V16] IndexedDB no disponible para offline_operations, usando localStorage');
      try {
        const existingRaw = _safeGetItem('offline_operations');
        const existing = existingRaw ? JSON.parse(existingRaw) : [];
        existing.push(op);
        const trimmed = existing.slice(-50);
        _safeSetItem('offline_operations', JSON.stringify(trimmed));
      } catch (e) {
        _safeSetItem('offline_operations', JSON.stringify([op]));
      }
      return { ...operation.data, _offline: true, _offlineId: op._offlineId };
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // 📊 ESTADO E CICLO DE VIDA
  // ═══════════════════════════════════════════════════════════════════════

  getStatus() {
    const quota = _checkStorageQuota();
    return {
      isSyncing: this.isSyncing, isOnline: this.isOnline, queueSize: this.queue.length,
      userId: this.userId, strategyType: this.strategyType,
      hasCloudStrategy: !!this.cloudStrategy, hasLocalStrategy: !!this.localStrategy,
      hasBeforePullHook: !!this._beforePullHook,
      tombstoneFilterActive: true, tombstoneTripleLayer: true,
      phantomDetector: true, autoRepair: true,
      autoRepairStats: this._autoRepairStats, tempIdRegistrySize: this._tempIdRegistry.size,
      storageUsage: `${(quota.usage * 100).toFixed(1)}%`, storageAvailable: quota.available,
      offlineOpsStorage: 'indexeddb', // 🔥 V16
      version: 'V16', lastUpdate: new Date().toISOString(),
    };
  }

  getQueue() { return [...this.queue]; }
  getQueueSize() { return this.queue.length; }
  clearQueue() { this.queue = []; }

  subscribe(observer) { this.observers.push(observer); return () => { this.observers = this.observers.filter(o => o !== observer); }; }
  notify(event, data) { for (const obs of this.observers) { try { if (typeof obs === 'function') obs(event, data); } catch (e) {} } }

  destroy() {
    this.stopSyncInterval(); this.clearQueue();
    if (this._tempIdCleanupInterval) clearInterval(this._tempIdCleanupInterval);
    if (typeof window !== 'undefined') { window.removeEventListener('online', this._onOnline); window.removeEventListener('offline', this._onOffline); }
    this.observers = []; this.idMapping.clear(); this.pendingCreates.clear();
    this.pullCooldown.clear(); this.recentlyExecuted.clear(); this.contentHash.clear();
    this.permissionDeniedDocs.clear(); this._globalThrottle.clear();
    this.recentlyModified.clear(); this._tombstonedIdsCache.clear();
    this._tempIdRegistry.clear(); this._beforePullHook = null;
  }
}

export default SyncManager;